/**
 * Linked trips: the staff banner at the top of a trip (server/trip-links.js,
 * migration 172).
 *
 * Founder 2026-10-08, on GEG50U and JZAHBD: "Add a feature for admin that can
 * tie multiple trips together. Just in like a reference sense, at the top of
 * each paired booking. So our team is very aware of the request."
 *
 * Shows the other trips this one travels with, the group's note ("Customer
 * wants to sit together"), how each was linked, and, when the customer said at
 * checkout that their companions are on another Defera booking, that answer
 * with the reference they typed. Admins link, unlink and edit the note here;
 * the concierge role reads it. Rendered for staff only; the customer never
 * sees it. Renders nothing when there is nothing to say and no form is open.
 *
 * Every trip is tagged Same account / Different account. Checkout only links
 * trips on one account (migration 173); someone else's trip is linked here,
 * and only after the admin confirms they checked with both travelers
 * (founder 2026-10-08: a reference alone proves nothing).
 */
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { format, parseISO } from 'date-fns';
import { Link2, Pencil, X, Check, Loader2, Plus, ExternalLink, Eye } from 'lucide-react';
import { TripLinks } from '@/api/entities';
import TravelingTogetherCard from './TravelingTogetherCard';
import { cn } from '@/lib/utils';

const NOTE_MAX = 500;

function fmtDay(value, pattern) {
  if (!value) return '';
  try {
    return format(parseISO(String(value)), pattern);
  } catch {
    return '';
  }
}

function linkedHow(trip) {
  const when = fmtDay(trip.linked_at, 'MMM d');
  if (trip.source === 'checkout') return `Linked at checkout${when ? ` · ${when}` : ''}`;
  return `Linked by ${trip.linked_by_name || 'an admin'}${when ? ` · ${when}` : ''}`;
}

/** Same account vs someone else's: seating strangers together needs both travelers' say-so. */
function AccountTag({ sameAccount }) {
  if (typeof sameAccount !== 'boolean') return null;
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-[11px] font-normal',
        sameAccount ? 'bg-white/10 text-white/70' : 'bg-amber-400/15 text-amber-200',
      )}
    >
      {sameAccount ? 'Same account' : 'Different account'}
    </span>
  );
}

AccountTag.propTypes = { sameAccount: PropTypes.bool };

function TripRow({ trip, canEdit, busy, onUnlink }) {
  const [confirming, setConfirming] = useState(false);
  const facts = [
    trip.travelers?.length ? trip.travelers.join(', ') : null,
    trip.route ? `${trip.route}${trip.round_trip ? ' round trip' : ''}` : null,
    trip.departs_on ? fmtDay(trip.departs_on, 'MMM d, yyyy') : null,
    trip.flights?.length ? trip.flights.join(' + ') : null,
  ].filter(Boolean);
  return (
    <li className="rounded-xl border border-white/10 bg-black/20 px-3 py-2.5">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={`/trips?tripId=${encodeURIComponent(trip.booking_id)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-mono text-sm font-medium text-white hover:text-amber-200 underline-offset-2 hover:underline"
              aria-label={`Open trip ${trip.reference} in a new tab`}
            >
              {trip.reference}
              <ExternalLink className="w-3 h-3 opacity-60" aria-hidden />
            </a>
            {trip.status === 'cancelled' && (
              <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-normal text-white/70">Cancelled</span>
            )}
            {typeof trip.same_flights === 'boolean' && (
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[11px] font-normal',
                  trip.same_flights ? 'bg-emerald-400/15 text-emerald-200' : 'bg-amber-400/15 text-amber-200',
                )}
              >
                {trip.same_flights ? 'Same flights' : 'Different flights'}
              </span>
            )}
            <AccountTag sameAccount={trip.same_account} />
          </div>
          {facts.length > 0 && (
            <p className="mt-1 text-[12.5px] font-normal text-white/75 leading-snug">{facts.join(' · ')}</p>
          )}
          <p className="mt-0.5 text-[11.5px] font-normal text-white/45">{linkedHow(trip)}</p>
        </div>
        {canEdit && (confirming ? (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              disabled={busy}
              onClick={() => onUnlink(trip)}
              className="rounded-lg bg-red-500/15 px-2.5 py-1 text-[12px] font-medium text-red-200 hover:bg-red-500/25 disabled:opacity-50"
            >
              Unlink
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirming(false)}
              className="rounded-lg px-2 py-1 text-[12px] font-normal text-white/60 hover:text-white"
            >
              Keep
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="flex-shrink-0 rounded-lg p-1 text-white/40 hover:text-white hover:bg-white/10"
            aria-label={`Unlink ${trip.reference}`}
            title={`Unlink ${trip.reference}`}
          >
            <X className="w-4 h-4" />
          </button>
        ))}
      </div>
    </li>
  );
}

TripRow.propTypes = {
  trip: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  busy: PropTypes.bool,
  onUnlink: PropTypes.func.isRequired,
};

/** What the customer said at checkout, when it isn't already a link here. */
function companionLine(request) {
  if (!request) return null;
  if (request.match) {
    return request.match.same_account === false
      ? `At checkout the customer said they're traveling with ${request.match.reference}, which is booked on a different account. Confirm with both travelers before linking them or seating them together.`
      : `At checkout the customer said they're traveling with ${request.match.reference}.`;
  }
  if (request.reference) {
    return `At checkout the customer gave ${request.reference} as their companions' booking, but no trip has that reference.`;
  }
  return 'At checkout the customer said their companions are on another Defera booking, without giving its reference.';
}

export default function LinkedTripsBanner({ bookingId, canEdit = false, formOpen = false, onFormOpenChange }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [editingNote, setEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  /** A link to someone else's trip waiting on "I've confirmed with both
   *  travelers": { reference, note, fromForm }. The server refuses it
   *  (409 different_account) until that confirmation comes with it. */
  const [confirmLink, setConfirmLink] = useState(null);
  /** The customer's "Traveling together" card, previewed for staff. */
  const [showCustomerCard, setShowCustomerCard] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    if (!bookingId) return undefined;
    TripLinks.forTrip(bookingId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { console.error('Linked trips failed to load (non-critical):', e); });
    return () => { cancelled = true; };
  }, [bookingId]);

  const closeForm = useCallback(() => {
    setReference('');
    setNote('');
    setError('');
    setConfirmLink((pending) => (pending?.fromForm ? null : pending));
    onFormOpenChange?.(false);
  }, [onFormOpenChange]);

  const run = useCallback(async (action) => {
    setBusy(true);
    setError('');
    try {
      setData(await action());
      return true;
    } catch (e) {
      setError(e?.message || 'Something went wrong');
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  /** Link to `ref`. Someone else's trip (known up front, or as the server
   *  answers) waits for the confirm step instead. */
  const attemptLink = async (ref, linkNote, { differentAccount = false, fromForm = false } = {}) => {
    setError('');
    if (differentAccount) { setConfirmLink({ reference: ref, note: linkNote, fromForm }); return; }
    setBusy(true);
    try {
      setData(await TripLinks.link(bookingId, ref, linkNote));
      setConfirmLink(null);
      if (fromForm) closeForm();
    } catch (e) {
      if (e?.code === 'different_account') setConfirmLink({ reference: ref, note: linkNote, fromForm });
      else setError(e?.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const confirmDifferentAccountLink = async () => {
    const pending = confirmLink;
    if (!pending) return;
    const linked = await run(() => TripLinks.link(bookingId, pending.reference, pending.note, { confirmDifferentAccount: true }));
    if (!linked) return;
    setConfirmLink(null);
    if (pending.fromForm) closeForm();
  };

  const submitLink = (e) => {
    e?.preventDefault?.();
    const ref = reference.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!ref) { setError('Enter the other trip\'s booking reference'); return; }
    attemptLink(ref, note.trim(), { fromForm: true });
  };

  const linkMatch = (t) => attemptLink(t.reference, '', { differentAccount: t.same_account === false });

  const unlink = (trip) => run(() => TripLinks.unlink(trip.booking_id, bookingId));

  const saveNote = async () => {
    if (await run(() => TripLinks.saveNote(bookingId, noteDraft.trim()))) setEditingNote(false);
  };

  const trips = data?.trips || [];
  const group = data?.group || null;
  const request = data?.companion_request || null;
  const pendingRequest = request && !request.linked ? request : null;
  if (!trips.length && !pendingRequest && !(canEdit && formOpen)) return null;

  const thisTrip = data?.this_trip || null;
  // What this trip's owner sees (mirrors loadCustomerTripLinks): their own
  // live linked trips by reference, anyone else's only as a count. A trip
  // whose account match is unknown counts as someone else's.
  const OFF = ['cancelled', 'defaulted'];
  const liveTrips = OFF.includes(String(data?.status || '').toLowerCase())
    ? []
    : trips.filter((t) => !OFF.includes(String(t.status || '').toLowerCase()));
  const customerOwnTrips = liveTrips
    .filter((t) => t.same_account === true)
    .map(({ booking_id: bookingIdOf, reference: ref, route, round_trip: roundTrip, departs_on: departsOn }) => ({
      booking_id: bookingIdOf, reference: ref, route, round_trip: roundTrip, departs_on: departsOn,
    }));
  const customerOtherBookings = liveTrips.filter((t) => t.same_account !== true).length;
  const customerSeesCard = customerOwnTrips.length + customerOtherBookings > 0;
  return (
    <section
      aria-label="Linked trips"
      className="mb-6 rounded-2xl border border-amber-300/25 bg-amber-300/[0.06] p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-300/15 ring-1 ring-inset ring-amber-300/35">
          <Link2 className="w-4 h-4 text-amber-200" strokeWidth={2.25} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] uppercase tracking-wider font-medium text-amber-200/90">Linked trips · staff only</p>
            {canEdit && !formOpen && (
              <button
                type="button"
                onClick={() => onFormOpenChange?.(true)}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-medium text-amber-100 hover:bg-amber-300/10"
              >
                <Plus className="w-3.5 h-3.5" aria-hidden /> Link a trip
              </button>
            )}
          </div>
          <h3 className="mt-1 text-[15px] font-medium text-white leading-tight">
            {trips.length
              ? `Traveling with ${trips.length === 1 ? 'another trip' : `${trips.length} other trips`}`
              : pendingRequest ? 'Traveling with another Defera booking' : 'Link this trip to another'}
          </h3>

          {/* The group's note: what the team should do about these trips. */}
          {group && (editingNote ? (
            <div className="mt-2 space-y-2">
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value.slice(0, NOTE_MAX))}
                rows={2}
                className="w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-[13px] text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                placeholder="e.g. Customer wants to sit together"
                aria-label="Note for the team"
              />
              <div className="flex items-center gap-2">
                <button type="button" disabled={busy} onClick={saveNote} className="inline-flex items-center gap-1 rounded-lg bg-amber-300/90 px-3 py-1.5 text-[12px] font-medium text-black hover:bg-amber-200 disabled:opacity-50">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save note
                </button>
                <button type="button" disabled={busy} onClick={() => setEditingNote(false)} className="rounded-lg px-2 py-1.5 text-[12px] font-normal text-white/60 hover:text-white">Cancel</button>
              </div>
            </div>
          ) : (group.note || canEdit) && (
            <div className="mt-1.5 flex items-start gap-1.5">
              {group.note
                ? <p className="text-[13px] font-normal text-amber-50/90 leading-relaxed whitespace-pre-line">{group.note}</p>
                : <p className="text-[13px] font-normal text-white/40">No note yet</p>}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => { setNoteDraft(group.note || ''); setEditingNote(true); }}
                  className="flex-shrink-0 rounded p-0.5 text-white/40 hover:text-white"
                  aria-label="Edit the note"
                  title="Edit the note"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}

          {trips.length > 0 && (
            <ul className="mt-3 space-y-2">
              {trips.map((t) => (
                <TripRow key={t.booking_id} trip={t} canEdit={canEdit} busy={busy} onUnlink={unlink} />
              ))}
            </ul>
          )}

          {customerSeesCard && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowCustomerCard((v) => !v)}
                aria-expanded={showCustomerCard}
                className="inline-flex items-center gap-1.5 text-[11.5px] font-normal text-white/50 hover:text-white/80"
              >
                <Eye className="w-3.5 h-3.5" aria-hidden />
                The customer sees a &ldquo;Traveling together&rdquo; card on this trip · {showCustomerCard ? 'Hide' : 'Preview'}
              </button>
              {showCustomerCard && (
                <div className="mt-2">
                  <TravelingTogetherCard
                    thisReference={data?.reference || null}
                    ownTrips={customerOwnTrips}
                    otherBookings={customerOtherBookings}
                    className="mb-0"
                  />
                </div>
              )}
            </div>
          )}

          {thisTrip?.source === 'checkout' && thisTrip.checkout_reference && (
            <p className="mt-2 text-[11.5px] font-normal text-white/45">
              This trip was linked at checkout: the customer entered {thisTrip.checkout_reference}.
            </p>
          )}

          {pendingRequest && (
            <div className="mt-3 rounded-xl border border-dashed border-amber-300/30 px-3 py-2.5">
              <p className="text-[12.5px] font-normal text-white/80 leading-snug">{companionLine(pendingRequest)}</p>
              {canEdit && pendingRequest.match && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => linkMatch(pendingRequest.match)}
                  className="mt-2 inline-flex items-center gap-1 rounded-lg bg-amber-300/90 px-3 py-1.5 text-[12px] font-medium text-black hover:bg-amber-200 disabled:opacity-50"
                >
                  <Link2 className="w-3.5 h-3.5" aria-hidden /> Link {pendingRequest.match.reference}
                </button>
              )}
              {pendingRequest.same_flight_trips?.length > 0 && (
                <div className="mt-2.5">
                  <p className="text-[11.5px] font-medium uppercase tracking-wider text-white/45">On the same first flight</p>
                  <ul className="mt-1.5 space-y-1.5">
                    {pendingRequest.same_flight_trips.map((t) => (
                      <li key={t.booking_id} className="flex items-center gap-2">
                        <a
                          href={`/trips?tripId=${encodeURIComponent(t.booking_id)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-[13px] font-medium text-white hover:text-amber-200 hover:underline underline-offset-2"
                        >
                          {t.reference}
                        </a>
                        <span className="min-w-0 flex-1 truncate text-[12px] font-normal text-white/60">
                          {[t.travelers?.join(', '), t.flights?.join(' + ')].filter(Boolean).join(' · ')}
                        </span>
                        <AccountTag sameAccount={t.same_account} />
                        {canEdit && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => linkMatch(t)}
                            className="flex-shrink-0 inline-flex items-center gap-1 rounded-lg border border-amber-300/40 px-2 py-0.5 text-[12px] font-medium text-amber-100 hover:bg-amber-300/10 disabled:opacity-50"
                          >
                            <Link2 className="w-3 h-3" aria-hidden /> Link
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {canEdit && formOpen && (
            <form onSubmit={submitLink} className="mt-3 rounded-xl border border-white/10 bg-black/20 p-3 space-y-2">
              <label className="block">
                <span className="text-[12px] font-medium text-white/80">Other trip&apos;s booking reference</span>
                <input
                  value={reference}
                  onChange={(e) => setReference(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="e.g. JZAHBD"
                  className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 font-mono text-sm tracking-wide text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                />
              </label>
              <label className="block">
                <span className="text-[12px] font-medium text-white/80">Note for the team (optional)</span>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
                  placeholder="e.g. Customer wants to sit together"
                  className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-[13px] text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                />
              </label>
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="submit"
                  disabled={busy || !reference}
                  className="inline-flex items-center gap-1 rounded-lg bg-amber-300/90 px-3 py-1.5 text-[12px] font-medium text-black hover:bg-amber-200 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Link trips
                </button>
                <button type="button" disabled={busy} onClick={closeForm} className="rounded-lg px-2 py-1.5 text-[12px] font-normal text-white/60 hover:text-white">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {canEdit && confirmLink && (
            <div role="alertdialog" aria-label="Confirm linking trips on different accounts" className="mt-3 rounded-xl border border-amber-300/40 bg-amber-300/[0.08] p-3">
              <p className="text-[12.5px] font-normal text-amber-50/90 leading-snug">
                {confirmLink.reference} is booked on a different account. Confirm with both travelers before linking these trips or seating them together.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={confirmDifferentAccountLink}
                  className="inline-flex items-center gap-1 rounded-lg bg-amber-300/90 px-3 py-1.5 text-[12px] font-medium text-black hover:bg-amber-200 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} I&apos;ve confirmed with both travelers
                </button>
                <button type="button" disabled={busy} onClick={() => setConfirmLink(null)} className="rounded-lg px-2 py-1.5 text-[12px] font-normal text-white/60 hover:text-white">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {error && <p role="alert" className="mt-2 text-[12px] font-normal text-red-300">{error}</p>}
        </div>
      </div>
    </section>
  );
}

LinkedTripsBanner.propTypes = {
  bookingId: PropTypes.string,
  canEdit: PropTypes.bool,
  formOpen: PropTypes.bool,
  onFormOpenChange: PropTypes.func,
};
